import { Injectable } from '@angular/core';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { EKpiGoalsStatusBadge } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-kpi-goals-table/cta-analytics-details-kpi-goals-table.interface';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import {
  IExpectedKpiApi,
  IKpiAnalyticsApi,
  IPlannedExecutionApi,
  TKpiAnalyticsKey,
} from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateService } from '@ngx-translate/core';
import { DATE_TIME_FORMAT, DateUtilitiesService } from '@utilities/date-utilities.service';
import { CampaignDetailsUndesignatedExecutionOrderService } from '../campaign-details-undesignated-execution-order.service';
import {
  ICampaignDetailsKpiGoalsFilterOption,
  ICampaignDetailsKpiGoalsFilterState,
  ICampaignDetailsKpiGoalsGroupedChildRow,
  ICampaignDetailsKpiGoalsGroupedParentRow,
  ICampaignDetailsKpiGoalsStatusOption,
  ICampaignDetailsKpiGoalsTableRow,
  IKpiGoalsSourceCta,
  TCampaignDetailsKpiGoalsGroupedRow,
} from './campaign-details-kpi-goals.interface';

interface IKpiGoalsRowContext {
  analyticsNode: IAnalyticsNode;
  dateLabel: string;
  dateSortValue: number;
  kpiAnalytics: IKpiAnalyticsApi | null | undefined;
  translate: TranslateService;
}

const KPI_STATUS_ORDER: EKpiGoalsStatusBadge[] = [
  EKpiGoalsStatusBadge.kpiGoalsUnavailable,
  EKpiGoalsStatusBadge.kpiGoalsBelowTarget,
  EKpiGoalsStatusBadge.kpiGoalsReached,
];

const EXPECTED_KPI_TO_ANALYTICS_KEY: Record<TExpectedKpi, TKpiAnalyticsKey> = {
  averageViewers: 'avgViewers',
  comments: 'comments',
  engagementRate: 'engagementRate',
  engagementRateViews: 'engagementRateViews',
  engagements: 'engagements',
  impressions: 'impressions',
  likes: 'likes',
  mediaValue: 'mediaValue',
  peakViewers: 'peakViewers',
  predictedImpressions: 'predictedImpressions',
  quotes: 'quotes',
  reach: 'reach',
  retweets: 'retweets',
  shares: 'shares',
  sponsoredAverageViewers: 'sponsoredAvgViewers',
  sponsoredPeakViewers: 'sponsoredPeakViewers',
  sponsoredTotalWatchedHours: 'sponsoredTotalWatchedHours',
  totalWatchedHours: 'totalWatchedHours',
  views: 'views',
};

@Injectable()
export class CampaignDetailsKpiGoalsService {
  constructor(
    private readonly dateUtils: DateUtilitiesService,
    private readonly undesignatedExecutionOrderService: CampaignDetailsUndesignatedExecutionOrderService,
  ) {}

  public buildFilterOptions(
    rows: ICampaignDetailsKpiGoalsTableRow[],
    translate: TranslateService,
  ): {
    ctaTemplateNames: ICampaignDetailsKpiGoalsFilterOption<string>[];
    kpiTypes: ICampaignDetailsKpiGoalsFilterOption<TExpectedKpi>[];
  } {
    const ctaTemplateNames = Array.from(new Set(rows.map((row) => row.ctaTemplateName)))
      .map((value) => ({ label: value, value }))
      .sort((left, right) => left.label.localeCompare(right.label));

    const kpiTypes = Array.from(new Set(rows.map((row) => row.kpiType)))
      .map((kpi) => ({
        label: translate.instant(`shared.expectedKpis.${kpi}`),
        value: kpi,
      }))
      .sort((left, right) => (left.label as string).localeCompare(right.label));

    return { ctaTemplateNames, kpiTypes };
  }

  public buildRows(
    analyticsNodes: IAnalyticsNode[] | null,
    translate: TranslateService,
  ): ICampaignDetailsKpiGoalsTableRow[] {
    if (!analyticsNodes?.length) {
      return [];
    }

    const fallbackUndesignatedOrderMap = this.undesignatedExecutionOrderService.buildOrderMap(analyticsNodes);
    const rows: ICampaignDetailsKpiGoalsTableRow[] = [];

    for (const analyticsNode of analyticsNodes) {
      const socialContent = analyticsNode.content?.socialContent ?? null;
      const kpiAnalytics = socialContent?.kpiAnalytics as IKpiAnalyticsApi | null | undefined;
      const executionTime = socialContent?.executionTime ?? analyticsNode.executionTime ?? null;
      const dateLabel = executionTime ? this.dateUtils.formatDate(new Date(executionTime), DATE_TIME_FORMAT) : '';
      const dateSortValue = executionTime ? new Date(executionTime).getTime() : 0;
      const matchedCtas = this.getMatchedSourceCtas(analyticsNode);
      const context: IKpiGoalsRowContext = { analyticsNode, dateLabel, dateSortValue, kpiAnalytics, translate };

      rows.push(...this.buildMatchedCtaRows(context, matchedCtas, fallbackUndesignatedOrderMap));
      rows.push(...this.buildPlannedNodeRows(context, matchedCtas, fallbackUndesignatedOrderMap));
    }

    return rows;
  }

  private buildMatchedCtaRows(
    context: IKpiGoalsRowContext,
    matchedCtas: IKpiGoalsSourceCta[],
    fallbackUndesignatedOrderMap: Map<string, number>,
  ): ICampaignDetailsKpiGoalsTableRow[] {
    const rows: ICampaignDetailsKpiGoalsTableRow[] = [];

    for (const sourceCta of matchedCtas) {
      const ctaResult = context.analyticsNode.content?.ctas.get(sourceCta.ctaId) ?? null;
      const plannedExecution = ctaResult?.plannedExecution ?? context.analyticsNode.plannedExecution ?? null;

      if (!plannedExecution?.expectedKpis?.length) {
        continue;
      }

      const resolvedContext = this.resolveContextForExecution(
        context,
        sourceCta.ctaId,
        plannedExecution,
        fallbackUndesignatedOrderMap,
      );

      for (const expectedKpi of plannedExecution.expectedKpis) {
        rows.push(this.buildKpiRow(resolvedContext, sourceCta.ctaId, sourceCta.ctaName, expectedKpi));
      }
    }

    return rows;
  }

  private buildPlannedNodeRows(
    context: IKpiGoalsRowContext,
    matchedCtas: IKpiGoalsSourceCta[],
    fallbackUndesignatedOrderMap: Map<string, number>,
  ): ICampaignDetailsKpiGoalsTableRow[] {
    const { analyticsNode } = context;

    if (
      matchedCtas.length !== 0 ||
      !analyticsNode.nonExecutedCtaId ||
      !analyticsNode.nonExecutedCtaName ||
      !analyticsNode.plannedExecution?.expectedKpis?.length
    ) {
      return [];
    }

    const ctaId = analyticsNode.nonExecutedCtaId;
    const ctaName = analyticsNode.nonExecutedCtaName;
    const { plannedExecution } = analyticsNode;
    const resolvedContext = this.resolveContextForExecution(
      context,
      ctaId,
      plannedExecution,
      fallbackUndesignatedOrderMap,
    );

    return plannedExecution.expectedKpis.map((expectedKpi) =>
      this.buildKpiRow(resolvedContext, ctaId, ctaName, expectedKpi),
    );
  }

  private resolveContextForExecution(
    context: IKpiGoalsRowContext,
    ctaId: string | null,
    plannedExecution: IPlannedExecutionApi,
    fallbackUndesignatedOrderMap: Map<string, number>,
  ): IKpiGoalsRowContext {
    if (context.dateSortValue > 0) {
      return context;
    }

    if (plannedExecution.executionType === EExecutionType.always) {
      return {
        ...context,
        dateLabel: context.translate.instant('pages.campaignDetails.deliverables.table.date.always'),
      };
    }

    if (plannedExecution.executionType === EExecutionType.undesignated) {
      const lookupKey = this.undesignatedExecutionOrderService.getLookupKey(
        context.analyticsNode.id,
        ctaId,
        plannedExecution,
      );
      const enrichedOrder = lookupKey
        ? (context.analyticsNode.undesignatedExecutionOrdersByLookupKey?.[lookupKey] ?? null)
        : null;
      const fallbackOrder = lookupKey ? (fallbackUndesignatedOrderMap.get(lookupKey) ?? null) : null;
      const executionOrder = enrichedOrder ?? fallbackOrder ?? plannedExecution.executionCountTarget ?? 0;

      return {
        ...context,
        dateLabel: context.translate.instant('pages.campaignDetails.deliverables.table.date.anyTime', {
          executionOrder,
        }),
      };
    }

    return context;
  }

  private buildKpiRow(
    context: IKpiGoalsRowContext,
    ctaId: string,
    ctaName: string,
    expectedKpi: IExpectedKpiApi,
  ): ICampaignDetailsKpiGoalsTableRow {
    const { analyticsNode, dateLabel, dateSortValue, kpiAnalytics, translate } = context;
    const kpiType = expectedKpi.kpi as TExpectedKpi;
    const currentValue = this.getKpiCurrentValue(kpiAnalytics, kpiType);
    const kpiGoalStatus = this.resolveKpiGoalStatus(currentValue, expectedKpi.expectedValue);

    return {
      analyticsNodeId: analyticsNode.id,
      creatorIcon: analyticsNode.creator.icon,
      creatorId: analyticsNode.creatorId,
      creatorName: analyticsNode.creator.name,
      ctaTemplateId: ctaId,
      ctaTemplateName: ctaName,
      currentValue,
      dateLabel,
      dateSortValue,
      expectedKpiId: expectedKpi.expectedKpiId,
      id: `${analyticsNode.id}__${ctaId}__${kpiType}`,
      kpiGoalStatus,
      kpiLabel: translate.instant(`shared.expectedKpis.${kpiType}`),
      kpiType,
      platformFeature: analyticsNode.platformFeature,
      rowStatusColorScheme: 'kpiGoals',
      statusSortValue: KPI_STATUS_ORDER.indexOf(kpiGoalStatus),
      targetValue: expectedKpi.expectedValue,
    };
  }

  public buildStatusOptions(
    rows: ICampaignDetailsKpiGoalsTableRow[],
    selectedStatuses: Set<EKpiGoalsStatusBadge>,
  ): ICampaignDetailsKpiGoalsStatusOption[] {
    return KPI_STATUS_ORDER.map(
      (status, index): ICampaignDetailsKpiGoalsStatusOption => ({
        colorScheme: 'kpiGoals',
        count: rows.filter((row) => row.kpiGoalStatus === status).length,
        isDisabled: rows.filter((row) => row.kpiGoalStatus === status).length === 0 && !selectedStatuses.has(status),
        key: status,
        sortOrder: index,
        status,
      }),
    );
  }

  public buildGroupedRows(rows: ICampaignDetailsKpiGoalsTableRow[]): TCampaignDetailsKpiGoalsGroupedRow[] {
    const groupedRowMap = new Map<string, TCampaignDetailsKpiGoalsGroupedRow>();

    for (const row of rows) {
      const groupId = `${row.creatorId}::${row.ctaTemplateId}::${row.dateSortValue > 0 ? row.dateSortValue : row.analyticsNodeId}`;
      const existingGroup = groupedRowMap.get(groupId);

      if (!existingGroup) {
        const parent: ICampaignDetailsKpiGoalsGroupedParentRow = {
          analyticsNodeId: row.analyticsNodeId,
          creatorIcon: row.creatorIcon,
          creatorId: row.creatorId,
          creatorName: row.creatorName,
          ctaTemplateId: row.ctaTemplateId,
          ctaTemplateName: row.ctaTemplateName,
          dateLabel: row.dateLabel,
          dateSortValue: row.dateSortValue,
          id: groupId,
          kpiCount: 0,
          matchingType: EMatchingType.matched,
          platformFeature: row.platformFeature,
        };

        groupedRowMap.set(groupId, {
          children: [],
          id: groupId,
          isExpanded: true,
          parent,
        });
      }

      const groupedRow = groupedRowMap.get(groupId);

      if (!groupedRow) {
        continue;
      }

      const childRow: ICampaignDetailsKpiGoalsGroupedChildRow = {
        id: row.id,
        kpiGoalStatus: row.kpiGoalStatus,
        rowStatusColorScheme: row.rowStatusColorScheme,
        sourceRow: row,
      };

      groupedRow.children.push(childRow);
      groupedRow.parent.kpiCount = groupedRow.children.length;
    }

    return Array.from(groupedRowMap.values());
  }

  public createInitialFilterState(): ICampaignDetailsKpiGoalsFilterState {
    return {
      ctaTemplateNames: new Set<string>(),
      kpiTypes: new Set<TExpectedKpi>(),
      statuses: new Set<EKpiGoalsStatusBadge>(),
    };
  }

  public filterRows(
    rows: ICampaignDetailsKpiGoalsTableRow[],
    filterState: ICampaignDetailsKpiGoalsFilterState,
  ): ICampaignDetailsKpiGoalsTableRow[] {
    return rows.filter((row) => {
      if (filterState.ctaTemplateNames.size > 0 && !filterState.ctaTemplateNames.has(row.ctaTemplateName)) {
        return false;
      }

      if (filterState.kpiTypes.size > 0 && !filterState.kpiTypes.has(row.kpiType)) {
        return false;
      }

      if (filterState.statuses.size > 0 && !filterState.statuses.has(row.kpiGoalStatus)) {
        return false;
      }

      return true;
    });
  }

  private getMatchedSourceCtas(analyticsNode: IAnalyticsNode): IKpiGoalsSourceCta[] {
    const contentCtas = Array.from(analyticsNode.content?.ctas?.values() ?? []);

    return contentCtas
      .filter((ctaResult) => ctaResult.matchingType === EMatchingType.matched)
      .map((ctaResult) => ({
        ctaId: ctaResult.ctaId,
        ctaName: ctaResult.name,
        matchingType: ctaResult.matchingType,
      }));
  }

  private getKpiCurrentValue(kpiAnalytics: IKpiAnalyticsApi | null | undefined, kpiType: TExpectedKpi): number | null {
    if (!kpiAnalytics) {
      return null;
    }

    const analyticsKey = EXPECTED_KPI_TO_ANALYTICS_KEY[kpiType];

    if (!analyticsKey) {
      return null;
    }

    const value = (kpiAnalytics as Record<string, number | null | undefined>)[analyticsKey];

    return value ?? null;
  }

  private resolveKpiGoalStatus(currentValue: number | null, targetValue: number): EKpiGoalsStatusBadge {
    if (currentValue === null) {
      return EKpiGoalsStatusBadge.kpiGoalsUnavailable;
    }

    return currentValue >= targetValue
      ? EKpiGoalsStatusBadge.kpiGoalsReached
      : EKpiGoalsStatusBadge.kpiGoalsBelowTarget;
  }
}
